import React from "react";
import { observer } from "mobx-react";
import { useEffect, useState, useRef } from "react";
import { RoundedButton, IconButton } from "../../../../../../../../elements";
import { useStore } from "../../../../../../../../hooks";
import {
  RuleHeader,
  FooterWrapper,
  RuleSeparator,
  HeaderWrapper,
  RulesViewWrapper,
  FiltersDivision,
  FiltersGenerator,
  IconButtonWrapper,
} from "./RulesView.styled";
import RuleEngine from "../../../../../components/RuleEngine/RuleEngine";
import PostRulesView from "./RulesViewVisualization";
import cloneDeep from "lodash/cloneDeep";
import Alerts from "../../../../../../../../elements/v2/Alerts/Alerts";
import { useUserCheckPermissions } from "../../../../../../../../hooks/useUserCheckPermissions";
import {createSearchParams} from "react-router-dom";
import {PermissionCodes} from "../../../../../../../../utils/enums";

const RulesView = observer(({ setShowContactMapping, showContactMapping, filters }) => {
  const { contactSegmentStore } = useStore();
  const { rulesStore } = contactSegmentStore;
  const [rules, setRules] = useState(null);
  const [alertInfo, setAlertInfo] = useState(null);
  const timerRef = useRef(null);
  const { checkPermission} = useUserCheckPermissions();

  useEffect(() => {
    if (filters) {
      rulesStore.loadFilters(filters);
      updateChanges();
      contactSegmentStore.updateAdvancedFilter();
    }
  }, [filters]);

  const onAddGroup = () => {
    const group = rulesStore.addGroup("OR");
    rulesStore.addRuleToGroup(group);
    rulesStore.setShowRightPreview(true);
  };

  const onImportCSV = () => {
    setShowContactMapping(!showContactMapping)
  };

  const onRemoveGroup = (group) => {
    rulesStore.removeGroup(group);
    updateChanges();
  };

  const onAddCondition = (group) => {
    rulesStore.addRuleToGroup(group);
  };


  useEffect(() => {
    const hasRulesGroup = Object.keys(rulesStore?.rulesGroups).length;
    rulesStore.setShowRightPreview(hasRulesGroup);
  }, [rules]);

  const validateRules = () => {
    return Object.values(rulesStore?.rulesGroups).some((group) => {
      return group?.conditions?.some(
        (condition) =>
          condition.sourceData?.length &&
          condition.sourceField?.length &&
          condition.operator?.length &&
          condition.targetValue?.length
      );
    });
  };

  const onApplyFilters = () => {
    rulesStore.saveRules();
    contactSegmentStore.updateAdvancedFilter();
    updateChanges();

    if (contactSegmentStore.contacts.length > 0) {
      setAlertInfo({
        severity: "info",
        title: "Loading contacts...",
        message: "Please wait."
      });
    } else {
      setAlertInfo({
        severity: "error",
        title: "No contacts match your selected criteria.",
        message: "Please adjust your filters."
      });
    }

    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    setTimeout(() => {
      setAlertInfo(null);
    }, 5000);
  };

  const updateChanges = () => {
    if (validateRules()) {
      setRules(cloneDeep(rulesStore));
    } else {
      setRules(
        cloneDeep({
          fields: [],
          message: "",
          operator: null,
          rulesGroups: {},
        })
      );
    }
  };

  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, []);

  return (
    <>
    <RulesViewWrapper showRightPreview={rulesStore.showRightPreview}>
      <p>All filters</p>
      <FiltersDivision>
        <FiltersGenerator showRightPreview={rulesStore.showRightPreview}>
          <HeaderWrapper>
            {!showContactMapping && checkPermission(PermissionCodes.ADD_FILTER_GROUP, "UPDATE") && (
              <RoundedButton
                width="80px"
                height="32px"
                fontSize="0.75em"
                onClick={onApplyFilters}
                kind={"blue"}
                disabled={!Object.keys(rulesStore.rulesGroups).length}
              >
                Apply
              </RoundedButton>
            )}
          </HeaderWrapper>
          {Object.keys(rulesStore.rulesGroups).map((group, idx) => (
            <div key={`group-${idx}`}>
              <RuleHeader>
                <p>Group {idx + 1}</p>
                <span>
                  <IconButtonWrapper onClick={() => onAddCondition(group)}>
                    <IconButton
                      icon="circleAdd"
                      width="16px"
                      height="16px"
                    />
                  </IconButtonWrapper>

                  <IconButtonWrapper onClick={() => onRemoveGroup(group)}>
                    <IconButton
                      icon="deleteOutline"
                      width="20px"
                      height="20px"
                    />
                  </IconButtonWrapper>
                </span>
              </RuleHeader>
              {rulesStore.rulesGroups[group].conditions.length > 0 &&
                rulesStore.rulesGroups[group].conditions.map(
                  (rule, index) => (
                    <React.Fragment key={`rule-${group}-${index}`}>
                      <RuleEngine
                        deleteIcon="deleteOutline"
                        className="segment-variant"
                        ruleGroupdID={group}
                        rule={rule}
                        index={index}
                        fields={rulesStore.fields}
                        ruleOperator={rulesStore.operator}
                        onChange={(info) =>
                          rulesStore.updateRule(info.index, info.data, group)
                        }
                        onClose={() => rulesStore.removeRule(index, group)}
                      />

                      {index < rulesStore.rulesGroups[group].conditions.length - 1 && (
                        <RuleSeparator>
                          <span></span>
                          <div> AND </div>
                          <span></span>
                        </RuleSeparator>
                      )}
                    </React.Fragment>
                  )
                )}
              <RuleSeparator>
                <span></span>
                <div> OR </div>
                <span></span>
              </RuleSeparator>
            </div>
          ))}
          <FooterWrapper>
            {!showContactMapping && checkPermission(PermissionCodes.ADD_FILTER_GROUP, "UPDATE") && (
              <RoundedButton width="160px" onClick={onAddGroup} kind={"blue"}>
                Add Filter Group
              </RoundedButton>
            )}

            {checkPermission(PermissionCodes.IMPORT_CSV, "UPDATE") && (
                <RoundedButton
                    width="160px"
                    onClick={onImportCSV}
                    kind={"outlined"}
                >
                  {showContactMapping ? "< Segment Table" : "Import CSV"}
                </RoundedButton>
            )}
          </FooterWrapper>
        </FiltersGenerator>
        {rulesStore.showRightPreview ? <PostRulesView rules={rules} /> : <></>}
      </FiltersDivision>
    </RulesViewWrapper>
      {alertInfo && (
        <Alerts
          severity={alertInfo.severity}
          title={alertInfo.title}
          message={alertInfo.message}
          width={"350px"}
        />
      )}
    </>
  );
});

export default RulesView;
